// src/utils/openHouseClash.js
// "Does this home already have an open house then?"
//
// Why (Henry, 09/24/2026): on 737 Fairmount Ave Ashley scheduled four open houses and Merri,
// not seeing them, scheduled the same four again. Every pair sent the sellers two sets of
// emails, and the twin nobody filled in kept asking Ashley for feedback for days. So the
// Schedule Open House dialog now warns before a second one is added.
//
// Pure on purpose: no React, no Firestore, no reads of its own. The caller already holds every
// open house on screen (the Open Houses page's allOpenHouses, the deal page tab's own rows) and
// hands the list in. Given nothing, this answers "no clash" and the warning simply never fires.
import { deriveStatus, formatTime } from "./openHouseHelpers";
import { mmddyyyy } from "./checkinSorting";

// "13:45" -> 825 minutes. Anything we cannot read comes back null, and a null makes the two
// windows clash on the date alone: we would rather warn once too often than miss the twin.
function toMinutes(t) {
  const m = /^\s*(\d{1,2}):(\d{2})\s*$/.exec(String(t ?? ""));
  if (!m) return null;
  const h = Number(m[1]);
  const min = Number(m[2]);
  if (h > 23 || min > 59) return null;
  return h * 60 + min;
}

// Do the two windows share a minute? Touching is not overlapping: 10:00 to 12:00 against
// 12:00 to 2:00 is two back-to-back open houses, which is fine.
function overlaps(aStart, aEnd, bStart, bEnd) {
  if (aStart === null || aEnd === null || bStart === null || bEnd === null) return true;
  return aStart < bEnd && aEnd > bStart;
}

// The open houses for the SAME home on the SAME date whose time overlaps the one being entered.
// Skips the record being edited (ignoreId) and anything Cancelled; a Completed one still counts,
// because a second open house in a slot that has already happened is still a duplicate.
export function findClashes(openHouses, { dealId, date, startTime, endTime, ignoreId } = {}) {
  if (!Array.isArray(openHouses) || !dealId || !date) return [];
  const newStart = toMinutes(startTime);
  const newEnd = toMinutes(endTime);
  return openHouses.filter((oh) => {
    if (!oh) return false;
    if (ignoreId !== undefined && ignoreId !== null && String(oh.id) === String(ignoreId)) return false;
    if (String(oh.dealId || "") !== String(dealId)) return false;
    if (String(oh.date || "") !== String(date)) return false;
    if (deriveStatus(oh) === "Cancelled") return false;
    return overlaps(newStart, newEnd, toMinutes(oh.startTime), toMinutes(oh.endTime));
  });
}

// "Ashley Olson and Merri Solstad". No em or en dashes, per house style.
function joinHosts(names) {
  if (!names.length) return "";
  if (names.length === 1) return names[0];
  return `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}`;
}

// The exact sentence the dialog shows, both in the amber line and in the confirmation:
//   "This home already has an open house then, hosted by Ashley Olson."
//   "This home already has 2 open houses then, hosted by Ashley Olson and Merri Solstad."
export function clashSentence(rows) {
  const list = Array.isArray(rows) ? rows.filter(Boolean) : [];
  if (!list.length) return "";
  const count =
    list.length === 1 ? "an open house" : `${list.length} open houses`;
  const names = list.map((r) => String(r.hostingAgentName || "").trim());
  // Only name the hosts when EVERY clashing open house has one. Counting two and naming one
  // would promise a name we do not have (a legacy or repaired record can carry no host).
  if (names.some((n) => !n)) return `This home already has ${count} then.`;
  const hosts = [];
  names.forEach((n) => {
    if (!hosts.includes(n)) hosts.push(n);
  });
  return `This home already has ${count} then, hosted by ${joinHosts(hosts)}.`;
}

// "09/23/2026, 12:00pm to 2:00pm" — the house date format (mmddyyyy, shared with the check-in
// sorter) and the times spelled out rather than dashed. The date is stored as YYYY-MM-DD and read
// straight off the string, so no time zone can shift it by a day.
export function clashWhen(oh) {
  if (!oh) return "";
  const day = mmddyyyy(oh.date);
  const start = clockTime(oh.startTime);
  const end = clockTime(oh.endTime);
  if (!day) return start && end ? `${start} to ${end}` : "";
  if (!start || !end) return day;
  return `${day}, ${start} to ${end}`;
}

// "14:00" -> "2:00pm", through the same formatter the open houses table uses, so the spelling can
// only ever change in one place. Guarded by our own parser first, because an unreadable time has
// to come back empty here rather than be echoed back out.
function clockTime(t) {
  return toMinutes(t) === null ? "" : formatTime(t);
}
